import React, { useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import { generateMixedMatches } from '../utils/matchGeneratorMixed';
import { generateAssaltiFederali } from '../utils/fencingLogic';

/**
 * Schermata di debug per testare la generazione degli assalti e dei match
 * per le diverse tipologie di competizione.
 */
const DebugGenerazione = () => {
  const [results, setResults] = useState({
    generateMixedMatches: null,
    generateAssaltiFederali: null,
    scoreKeeperOutput: null,
    teamsInfo: null,
    currentType: localStorage.getItem('competitionType') || 'individual'
  });

  // Recupera i dati dalla configurazione corrente
  const fetchCurrentConfig = () => {
    try {
      const competitionType = localStorage.getItem('competitionType') || 'individual';
      const teams = JSON.parse(localStorage.getItem('teams') || '[]');
      const matches = JSON.parse(localStorage.getItem('matches') || '[]');

      // Recupera membri delle squadre
      let members = [];
      if (Array.isArray(teams)) {
        teams.forEach(team => {
          if (Array.isArray(team.members)) {
            members.push(...team.members);
          }
        });
      }

      // Info squadre
      const teamsInfo = {
        totalTeams: teams.length,
        teams: teams.map(t => ({
          name: t.name,
          membersCount: Array.isArray(t.members) ? t.members.length : 0
        })),
        totalMembers: members.length
      };

      // Info match
      const matchesInfo = {
        totalMatches: matches.length,
        matchesWithAssalti: matches.filter(m => Array.isArray(m.assalti) && m.assalti.length > 0).length,
        categories: [...new Set(matches.map(m => m.categoria))].filter(Boolean)
      };

      // Aggiorna i risultati
      setResults({
        ...results,
        teamsInfo,
        matchesInfo,
        currentType: competitionType
      });

    } catch (e) {
      console.error("Errore nel recupero configurazione", e);
    }
  };

  // Simula la generazione di match misti
  const testMixedMatchGeneration = () => {
    try {
      const teams = JSON.parse(localStorage.getItem('teams') || '[]');
      const savedConfig = JSON.parse(localStorage.getItem('mixed_config') || '{}');
      const categories = savedConfig.categories || [];
      const stoccate = savedConfig.stoccate || 5;

      // Genera i match normali
      const normalMatches = generateMixedMatches({ teams, categories, stoccate, alternate: true });

      // Genera assalti per il primo match
      let assalti = [];
      if (normalMatches.length > 0 && teams.length >= 2) {
        // Simula lo stesso comportamento di ScoreKeeper
        const teamA = teams[0];
        const teamB = teams[1];

        if (teamA && teamB && teamA.members && teamB.members) {
          assalti = generateAssaltiFederali(teamA.members, teamB.members);
        }
      }

      // Genera output speciale per caso misto con categorie unite
      const specialCaseOutput = teams.length === 2 && categories.length > 0 ? [
        {
          squadraA: teams[0].name || 'Squadra 1',
          squadraB: teams[1].name || 'Squadra 2',
          categoria: categories.join(','),
          stoccate,
          punteggioA: 0,
          punteggioB: 0,
          stato: 'da disputare',
          note: '',
          id: `0_1_${categories.join('_')}`
        }
      ] : [];

      // Aggiorna i risultati
      setResults({
        ...results,
        generateMixedMatches: {
          input: { teamsCount: teams.length, categories, stoccate },
          outputRegular: normalMatches,
          outputSpecialCase: specialCaseOutput
        },
        generateAssaltiFederali: {
          input: {
            teamA: teams[0]?.name || 'N/A',
            teamB: teams[1]?.name || 'N/A',
            membersA: teams[0]?.members?.length || 0,
            membersB: teams[1]?.members?.length || 0
          },
          output: assalti
        }
      });

    } catch (e) {
      console.error("Errore nella generazione test", e);
    }
  };

  // Simula la generazione di assalti ScoreKeeper su mixed match con categorie unite
  const testScoreKeeperSpecialCase = () => {
    try {
      const matches = JSON.parse(localStorage.getItem('matches') || '[]');
      if (matches.length === 0) {
        alert('Nessun match disponibile');
        return;
      }

      // Prendi il primo match per il test
      const testMatch = matches[0];

      // Verifica se è un match misto con categorie unite
      const isMixedMultiCategory =
        localStorage.getItem('competitionType') === 'mixed' &&
        testMatch &&
        testMatch.categoria &&
        testMatch.categoria.includes(',');

      // Crea output simulato ScoreKeeper
      let simulatedAssalti = [];
      if (isMixedMultiCategory) {
        simulatedAssalti = [{
          fencerA: testMatch.squadraA || 'Squadra A',
          fencerB: testMatch.squadraB || 'Squadra B',
          id: '0_0',
          scoreA: 0,
          scoreB: 0
        }];
      } else {
        // Sarebbe generato da generateAssaltiFederali, ma qui usiamo un valore di esempio
        simulatedAssalti = [
          { fencerA: 'Default A1', fencerB: 'Default B1', id: '0_0', scoreA: 0, scoreB: 0 },
          { fencerA: 'Default A2', fencerB: 'Default B2', id: '1_1', scoreA: 0, scoreB: 0 }
        ];
      }

      // Aggiorna i risultati
      setResults({
        ...results,
        scoreKeeperOutput: {
          match: {
            id: testMatch.id,
            squadraA: testMatch.squadraA,
            squadraB: testMatch.squadraB,
            categoria: testMatch.categoria,
            isMixedMultiCategory
          },
          simulatedAssalti
        }
      });

    } catch (e) {
      console.error("Errore nella simulazione ScoreKeeper", e);
    }
  };

  // Ripulisce i risultati visualizzati
  const resetResults = () => {
    setResults({
      generateMixedMatches: null,
      generateAssaltiFederali: null,
      scoreKeeperOutput: null,
      teamsInfo: null,
      currentType: localStorage.getItem('competitionType') || 'individual'
    });
  };

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>Debug Generazione Match e Assalti</Text>

      <View style={styles.infoCard}>
        <Text style={styles.cardTitle}>Tipo di competizione corrente</Text>
        <Text style={styles.infoText}>{results.currentType || 'N/A'}</Text>
      </View>

      <View style={styles.buttonsContainer}>
        <TouchableOpacity style={styles.button} onPress={fetchCurrentConfig}>
          <Text style={styles.buttonText}>Carica configurazione attuale</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.button} onPress={testMixedMatchGeneration}>
          <Text style={styles.buttonText}>Test generazione match misti</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.button} onPress={testScoreKeeperSpecialCase}>
          <Text style={styles.buttonText}>Test comportamento ScoreKeeper</Text>
        </TouchableOpacity>

        <TouchableOpacity style={[styles.button, { backgroundColor: '#d32f2f' }]} onPress={resetResults}>
          <Text style={styles.buttonText}>Pulisci</Text>
        </TouchableOpacity>
      </View>

      {results.teamsInfo && (
        <View style={styles.resultCard}>
          <Text style={styles.cardTitle}>Informazioni Squadre</Text>
          <Text style={styles.jsonOutput}>
            {JSON.stringify(results.teamsInfo, null, 2)}
          </Text>
        </View>
      )}

      {results.matchesInfo && (
        <View style={styles.resultCard}>
          <Text style={styles.cardTitle}>Informazioni Match</Text>
          <Text style={styles.jsonOutput}>
            {JSON.stringify(results.matchesInfo, null, 2)}
          </Text>
        </View>
      )}

      {results.generateMixedMatches && (
        <View style={styles.resultCard}>
          <Text style={styles.cardTitle}>Test generateMixedMatches</Text>
          <Text style={styles.subTitle}>Input:</Text>
          <Text style={styles.jsonOutput}>
            {JSON.stringify(results.generateMixedMatches.input, null, 2)}
          </Text>

          <Text style={styles.subTitle}>Output Regolare:</Text>
          <Text style={styles.jsonOutput}>
            {JSON.stringify(results.generateMixedMatches.outputRegular, null, 2)}
          </Text>

          <Text style={styles.subTitle}>Output Caso Speciale (2 squadre):</Text>
          <Text style={styles.jsonOutput}>
            {JSON.stringify(results.generateMixedMatches.outputSpecialCase, null, 2)}
          </Text>
        </View>
      )}

      {results.generateAssaltiFederali && (
        <View style={styles.resultCard}>
          <Text style={styles.cardTitle}>Test generateAssaltiFederali</Text>
          <Text style={styles.subTitle}>Input:</Text>
          <Text style={styles.jsonOutput}>
            {JSON.stringify(results.generateAssaltiFederali.input, null, 2)}
          </Text>

          <Text style={styles.subTitle}>Output:</Text>
          <Text style={styles.jsonOutput}>
            {JSON.stringify(results.generateAssaltiFederali.output, null, 2)}
          </Text>
          <Text style={styles.subTitle}>Numero assalti: {results.generateAssaltiFederali.output.length}</Text>
        </View>
      )}

      {results.scoreKeeperOutput && (
        <View style={styles.resultCard}>
          <Text style={styles.cardTitle}>Simulazione Output ScoreKeeper</Text>
          <Text style={styles.subTitle}>Match:</Text>
          <Text style={styles.jsonOutput}>
            {JSON.stringify(results.scoreKeeperOutput.match, null, 2)}
          </Text>

          <Text style={styles.subTitle}>Assalti Simulati:</Text>
          <Text style={styles.jsonOutput}>
            {JSON.stringify(results.scoreKeeperOutput.simulatedAssalti, null, 2)}
          </Text>
          <Text style={styles.subTitle}>Numero assalti: {results.scoreKeeperOutput.simulatedAssalti.length}</Text>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: '#f8f8ff',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 20,
    textAlign: 'center',
  },
  buttonsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 20,
  },
  button: {
    backgroundColor: '#1976d2',
    borderRadius: 8,
    padding: 12,
    minWidth: 150,
    alignItems: 'center',
    margin: 5,
  },
  buttonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 14,
  },
  infoCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 20,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    borderLeftWidth: 5,
    borderLeftColor: '#42a5f5',
  },
  resultCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 20,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    borderLeftWidth: 5,
    borderLeftColor: '#66bb6a',
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  subTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#555',
    marginTop: 10,
    marginBottom: 5,
  },
  infoText: {
    fontSize: 16,
    color: '#333',
  },
  jsonOutput: {
    backgroundColor: '#f5f5f5',
    padding: 10,
    borderRadius: 5,
    fontFamily: 'monospace',
    fontSize: 12,
    color: '#333',
    marginBottom: 10,
  }
});

export default DebugGenerazione;
